import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TimerIcon from '@mui/icons-material/Timer';

import { DateTime } from 'luxon';

export const Timelog = ({ shift, timelog }) => {
    // Helper function to determine if clock in/out is late
    const isClockInLate = timelog?.started &&
        DateTime.fromMillis(timelog.started).diff(DateTime.fromMillis(shift.start), 'minutes').minutes > 15;

    const isClockOutLate = timelog?.stopped &&
        DateTime.fromMillis(timelog.stopped).diff(DateTime.fromMillis(shift.end), 'minutes').minutes > 15;

    // Calculate total time
    const calculateTotalTime = () => {
        if (!timelog?.started) return null;

        const startTime = DateTime.fromMillis(timelog.started);
        const endTime = timelog?.stopped ?
            DateTime.fromMillis(timelog.stopped) :
            DateTime.now();

        const duration = endTime.diff(startTime, [ 'hours', 'minutes' ]);
        const hours = Math.floor(duration.hours);
        const minutes = Math.floor(duration.minutes % 60);

        return { hours, minutes, isActive: !timelog?.stopped };
    };

    const totalTime = calculateTotalTime();

    return (
        <Card sx={ {
            bgcolor: 'rgba(243, 177, 102, 0.08)',
            border: '1px solid rgba(243, 177, 102, 0.2)',
            borderRadius: 2
        } }>
            <CardContent sx={ { pb: 2 } }>
                <Typography variant="h6" sx={ {
                    color: 'white',
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                } }>
                    <AccessTimeIcon fontSize="small" />
                    Time Log - { timelog?.patrollerName || '' }
                </Typography>

                <Stack spacing={ 2 }>
                    {/* Clock In Row */ }
                    <Box sx={ {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1.5,
                        bgcolor: timelog?.started ?
                            (isClockInLate ? 'rgba(255, 152, 0, 0.1)' : 'rgba(76, 175, 80, 0.1)') :
                            'rgba(244, 67, 54, 0.1)',
                        borderRadius: 1,
                        border: `1px solid ${ timelog?.started ?
                            (isClockInLate ? 'rgba(255, 152, 0, 0.3)' : 'rgba(76, 175, 80, 0.3)') :
                            'rgba(244, 67, 54, 0.3)' }`
                    } }>
                        <Box sx={ { display: 'flex', alignItems: 'center', gap: 1 } }>
                            <LoginIcon
                                fontSize="small"
                                sx={ {
                                    color: timelog?.started ?
                                        (isClockInLate ? 'warning.main' : 'success.main') :
                                        'error.main'
                                } }
                            />
                            <Typography variant="body1" sx={ { color: 'white', fontWeight: 500 } }>
                                Clock In
                            </Typography>
                        </Box>

                        { timelog?.started && DateTime.fromMillis(timelog.started).isValid ? (
                            <Chip
                                label={ DateTime.fromMillis(timelog.started).toLocaleString(DateTime.TIME_SIMPLE) }
                                size="small"
                                sx={ {
                                    bgcolor: isClockInLate ? 'warning.main' : 'success.main',
                                    color: 'white',
                                    fontWeight: 600,
                                    fontVariantNumeric: 'tabular-nums'
                                } }
                            />
                        ) : (
                            <Chip
                                icon={ <WarningAmberIcon fontSize="small" /> }
                                label="Missing"
                                size="small"
                                color="error"
                                variant="outlined"
                            />
                        ) }
                    </Box>

                    {/* Clock Out Row */ }
                    <Box sx={ {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1.5,
                        bgcolor: timelog?.stopped ?
                            (isClockOutLate ? 'rgba(255, 152, 0, 0.1)' : 'rgba(76, 175, 80, 0.1)') :
                            (DateTime.now().toMillis() < shift.end ? 'rgba(33, 150, 243, 0.1)' : 'rgba(244, 67, 54, 0.1)'),
                        borderRadius: 1,
                        border: timelog?.stopped ?
                            `1px solid ${ isClockOutLate ? 'rgba(255, 152, 0, 0.3)' : 'rgba(76, 175, 80, 0.3)' }` :
                            (DateTime.now().toMillis() < shift.end ? '1px solid rgba(33, 150, 243, 0.3)' : '1px solid rgba(244, 67, 54, 0.3)')
                    } }>
                        <Box sx={ { display: 'flex', alignItems: 'center', gap: 1 } }>
                            <LogoutIcon
                                fontSize="small"
                                sx={ {
                                    color: timelog?.stopped ?
                                        (isClockOutLate ? 'warning.main' : 'success.main') :
                                        (DateTime.now().toMillis() < shift.end ? 'info.main' : 'error.main')
                                } }
                            />
                            <Typography variant="body1" sx={ { color: 'white', fontWeight: 500 } }>
                                Clock Out
                            </Typography>
                        </Box>

                        { timelog?.stopped && DateTime.fromMillis(timelog.stopped).isValid ? (
                            <Chip
                                label={ DateTime.fromMillis(timelog.stopped).toLocaleString(DateTime.TIME_SIMPLE) }
                                size="small"
                                sx={ {
                                    bgcolor: isClockOutLate ? 'warning.main' : 'success.main',
                                    color: 'white',
                                    fontWeight: 600,
                                    fontVariantNumeric: 'tabular-nums'
                                } }
                            />
                        ) : DateTime.now().toMillis() < shift.end ? (
                            <Chip
                                label="Shift Active"
                                size="small"
                                sx={ {
                                    bgcolor: 'info.main',
                                    color: 'white',
                                    fontWeight: 600
                                } }
                            />
                        ) : (
                            <Chip
                                icon={ <WarningAmberIcon fontSize="small" /> }
                                label="Missing"
                                size="small"
                                color="error"
                                variant="outlined"
                            />
                        ) }
                    </Box>

                    {/* Total Time Row */ }
                    { totalTime && (
                        <>
                            <Divider sx={ { bgcolor: 'rgba(33, 150, 243, 0.3)' } } />
                            <Box sx={ {
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 1.5,
                                bgcolor: 'rgba(33, 150, 243, 0.1)',
                                borderRadius: 1,
                                border: '1px solid rgba(33, 150, 243, 0.3)'
                            } }>
                                <Box sx={ { display: 'flex', alignItems: 'center', gap: 1 } }>
                                    <TimerIcon
                                        fontSize="small"
                                        sx={ { color: 'info.main' } }
                                    />
                                    <Typography variant="body1" sx={ { color: 'white', fontWeight: 600 } }>
                                        Total Time
                                    </Typography>
                                </Box>

                                <Chip
                                    label={ `${ totalTime.hours }h ${ totalTime.minutes }m${ totalTime.isActive ? ' (active)' : '' }` }
                                    size="small"
                                    sx={ {
                                        bgcolor: 'info.main',
                                        color: 'white',
                                        fontWeight: 700,
                                        fontVariantNumeric: 'tabular-nums'
                                    } }
                                />
                            </Box>
                        </>
                    ) }
                </Stack>
            </CardContent>
        </Card>
    )
};